import { initializeApp, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";

function readEnv(key: string): string {
  const value = import.meta.env[key];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`Missing required env var: ${key}`);
  }
  return value;
}

/**
 * 画像を置いているバケットを指しているかを起動時に検査する。
 *
 * @what: `<project>.firebasestorage.app` / `<project>.appspot.com` のような Firebase 既定の
 *   バケット名なら例外にする。
 * @why: backend (Cloud Run) が画像を書くのは Secret Manager `storage_bucket_name` のバケット
 *   (= `<project>-assets`) で、Firebase 既定のバケットとは別物。storage.rules も assets 側
 *   にだけ適用される。既定値を設定すると SDK が空のバケットを見て object-not-found になり、
 *   **画面には「読み込めません」としか出ないため原因にたどり着けない**。Flutter で PR #130、
 *   web で 2026-08-22 に同じ事故を起こしたので、設定の時点で落とす。
 * @failure: 既定バケット名だと Error を投げてアプリが起動しない (沈黙して画像だけ欠けるより良い)。
 */
export function assertMediaBucket(bucket: string): string {
  if (/\.(firebasestorage\.app|appspot\.com)$/.test(bucket)) {
    throw new Error(
      `VITE_FIREBASE_STORAGE_BUCKET が Firebase 既定のバケット (${bucket}) を指しています。` +
        `画像は backend が書く <project>-assets バケットにあります。.env.production.example を参照してください。`,
    );
  }
  return bucket;
}

function buildOptions(): FirebaseOptions {
  return {
    apiKey: readEnv("VITE_FIREBASE_API_KEY"),
    authDomain: readEnv("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: readEnv("VITE_FIREBASE_PROJECT_ID"),
    storageBucket: assertMediaBucket(readEnv("VITE_FIREBASE_STORAGE_BUCKET")),
    messagingSenderId: readEnv("VITE_FIREBASE_MESSAGING_SENDER_ID"),
    appId: readEnv("VITE_FIREBASE_APP_ID"),
  };
}

let appInstance: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let firestoreInstance: Firestore | null = null;
let storageInstance: FirebaseStorage | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (!appInstance) {
    appInstance = initializeApp(buildOptions());
  }
  return appInstance;
}

export function getFirebaseAuth(): Auth {
  if (!authInstance) {
    authInstance = getAuth(getFirebaseApp());
  }
  return authInstance;
}

export function getFirebaseFirestore(): Firestore {
  if (!firestoreInstance) {
    firestoreInstance = getFirestore(getFirebaseApp());
  }
  return firestoreInstance;
}

export function getFirebaseStorage(): FirebaseStorage {
  if (!storageInstance) {
    storageInstance = getStorage(getFirebaseApp());
  }
  return storageInstance;
}
